import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'

import { Helmet } from 'react-helmet-async'

import * as requestTaskActions from '../requestTaskActions.js'

import EmailInput from '../../../global/components/forms/EmailInput.jsx'
import AlertModal from '../../../global/components/modals/AlertModal.jsx'
import ShareLinkNav from '../../shareLink/components/ShareLinkNav.jsx'
import SingleRequestTask from '../components/SingleRequestTask.jsx'

import UISwitchChecker from '../../../global/practice/components/PracticeUISwitcher.jsx'
import apiUtils from '../../../global/utils/api.js'
import { jwtDecode } from 'jwt-decode'
import { PUBLIC_TOKEN_KEY } from '../../../config/constants.js'

const ViewRequestTask = () => {
  const dispatch = useDispatch()
  const params = useParams()

  const loggedInUser = useSelector(state => state.user.loggedIn.user)
  const requestTaskStore = useSelector(state => state.requestTask)

  const [state, setState] = useState({
    userEmail: '',
    twofaCode: '',
    authenticated: false,
    alertModalOpen: false,
    wrongUser: false,
    emailError: null,
    sendingCode: false,
    verifyingCode: false,
    codeSent: false,
    twofaError: null,
  })

  useEffect(() => {
    try {
      const token = window.localStorage.getItem(PUBLIC_TOKEN_KEY)
      if (token) {
        const decoded = jwtDecode(token)
        const now = Date.now() / 1000
        if (decoded.exp && decoded.exp < now) {
          console.log('JWT expired, removing from local storage')
          window.localStorage.removeItem(PUBLIC_TOKEN_KEY)
        } else {
          setState(prev => ({ ...prev, authenticated: true }))
        }
      }
    } catch (err) {
      console.error('Failed to validate JWT', err)
      window.localStorage.removeItem(PUBLIC_TOKEN_KEY)
    }

    dispatch(requestTaskActions.fetchSingleByHex(params.hex)).then(json => {
      if (json.success && loggedInUser && loggedInUser.username) {
        handleCheckUserEmail(loggedInUser.username, true)
      }
    })
  }, [])

  const handleCheckUserEmail = async (username, fromMount) => {
    if (state.emailError) return
    const selectedRequestTask = requestTaskStore.selectedHex.getItem()
    if (!selectedRequestTask || !selectedRequestTask.hex) return
    // Attempt to send code immediately
    await handleSendCode(selectedRequestTask.hex, username, fromMount)
  }

  const handleFormChange = e => {
    setState(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSendCode = async (hex, email, fromMount) => {
    try {
      setState(prev => ({ ...prev, sendingCode: true, twofaError: null }))
      const res = await apiUtils.callAPI('/api/2fa/send-code', 'POST', {
        context: 'request-task',
        reference: hex,
        email,
      })
      if (res && res.success) {
        setState(prev => ({ ...prev, codeSent: true, userEmail: email }))
      } else {
        if (fromMount) {
          setState(prev => ({ ...prev, wrongUser: true }))
        } else {
          setState(prev => ({
            ...prev,
            alertModalOpen: true,
            twofaError: res.message,
          }))
        }
      }
    } catch (e) {
      setState(prev => ({
        ...prev,
        twofaError: e.message,
        alertModalOpen: true,
      }))
    } finally {
      setState(prev => ({ ...prev, sendingCode: false }))
    }
  }

  const handleVerifyCode = async () => {
    const { twofaCode, userEmail } = state
    const selectedRequestTask = requestTaskStore.selectedHex.getItem()
    if (!(selectedRequestTask && selectedRequestTask.hex)) return
    try {
      setState(prev => ({ ...prev, verifyingCode: true, twofaError: null }))
      const res = await apiUtils.callAPI('/api/2fa/verify-code', 'POST', {
        context: 'request-task',
        reference: selectedRequestTask.hex,
        email: userEmail,
        code: twofaCode,
      })
      if (res && res.success && res.token) {
        // persist token
        try {
          window.localStorage.setItem(PUBLIC_TOKEN_KEY, res.token)
        } catch (e) {
          console.log(e)
        }
        setState(prev => ({ ...prev, authenticated: true }))
      } else {
        setState(prev => ({
          ...prev,
          twofaError: res?.message || 'Invalid code',
        }))
      }
    } catch (e) {
      setState(prev => ({ ...prev, twofaError: e.message }))
    } finally {
      setState(prev => ({ ...prev, verifyingCode: false }))
    }
  }

  const handleResendCode = async () => {
    const { userEmail } = state
    const selectedRequestTask = requestTaskStore.selectedHex.getItem()
    if (!(selectedRequestTask && selectedRequestTask.hex)) return
    await handleSendCode(selectedRequestTask.hex, userEmail)
  }

  const handleReload = () => {
    dispatch(requestTaskActions.fetchSingleByHex(params.hex))
    setState(prev => ({ ...prev, alertModalOpen: false, userEmail: '' }))
  }

  const {
    userEmail,
    twofaCode,
    authenticated,
    alertModalOpen,
    wrongUser,
    emailError,
    sendingCode,
    verifyingCode,
    codeSent,
    twofaError,
  } = state

  const selectedRequestTask = requestTaskStore.selectedHex.getItem()

  const isEmpty =
    !selectedRequestTask ||
    !selectedRequestTask._id ||
    requestTaskStore.selectedHex.didInvalidate

  const isFetching = requestTaskStore.selectedHex.isFetching

  return (
    <div>
      <UISwitchChecker />
      <Helmet>
        <title>Request Task</title>
      </Helmet>
      {isEmpty ? (
        isFetching ? (
          <div className="-loading-hero hero">
            <div className="u-centerText">
              <div className="loading"></div>
            </div>
          </div>
        ) : (
          <div className="flex column">
            <section className="section white-bg the-404">
              <div className="hero flex three-quarter ">
                <div className="yt-container slim">
                  <h1>
                    {' '}
                    Whoops!{' '}
                    <span className="light-weight">Something wrong here</span>
                  </h1>
                  <hr />
                  <h4>
                    Either this link no longer exists, or your credentials are
                    invalid.
                  </h4>
                </div>
              </div>
            </section>
          </div>
        )
      ) : (
        <div className="share-link-layout">
          <ShareLinkNav />
          {wrongUser ? (
            <div
              className="yt-row center-horiz"
              style={{ marginTop: '128px' }}
            >
              <div className="yt-container slim">
                <h1>
                  {' '}
                  Whoops!{' '}
                  <span className="light-weight">
                    You're not assigned user on this link
                  </span>
                </h1>
                <hr />
                <h4>
                  Do you have multiple accounts? You may be logged in to the
                  wrong one. You can{' '}
                  <Link to="/user/forward">switch accounts.</Link>
                </h4>
              </div>
            </div>
          ) : (
            <div className="body with-header">
              <div className="yt-container slim">
                <h3>
                  {selectedRequestTask.firm ? (
                    <span> {selectedRequestTask.firm.name} </span>
                  ) : (
                    <span> Your accountant </span>
                  )}
                  is requesting your file
                </h3>
                {selectedRequestTask.client ? (
                  <p className="u-muted">
                    for {selectedRequestTask.client.name}
                  </p>
                ) : null}
                <hr />
                {authenticated ? (
                  <div>
                    <SingleRequestTask
                      fromShareLink={true}
                      userEmail={userEmail}
                      requestTask={selectedRequestTask}
                      selectedFirm={
                        selectedRequestTask && selectedRequestTask.firm
                          ? selectedRequestTask.firm
                          : {}
                      }
                    />
                  </div>
                ) : (
                  <div className=" yt-col full m_50 l_40 xl_33">
                    {!codeSent ? (
                      <div>
                        <EmailInput
                          autoFocus
                          name="userEmail"
                          placeholder="Enter your email address"
                          change={handleFormChange}
                          value={userEmail}
                          error={emailError || twofaError}
                          errorName="emailError"
                        />
                        <div className="input-group">
                          <button
                            className="yt-btn x-small info u-pullRight"
                            onClick={() => handleCheckUserEmail(userEmail)}
                            disabled={!userEmail || sendingCode}
                          >
                            {sendingCode ? 'Sending...' : 'Send Code'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="input-group">
                          <input
                            type="text"
                            name="twofaCode"
                            placeholder="Enter the 6-digit code"
                            className="yt-input"
                            onChange={handleFormChange}
                            value={twofaCode}
                          />
                        </div>
                        {twofaError ? (
                          <p
                            className="u-danger"
                            style={{ marginTop: '6px' }}
                          >
                            {twofaError}
                          </p>
                        ) : null}
                        <div className="input-group">
                          <button
                            className="yt-btn x-small info u-pullRight"
                            onClick={handleVerifyCode}
                            disabled={!twofaCode || verifyingCode}
                          >
                            {verifyingCode ? 'Verifying...' : 'Verify'}
                          </button>
                          <button
                            className="yt-btn x-small link"
                            onClick={handleResendCode}
                            disabled={sendingCode}
                          >
                            Resend code
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <AlertModal
            alertMessage={
              <div>
                <p>
                  You did not enter the correct information to access this link.
                </p>
              </div>
            }
            alertTitle="Invalid credentials"
            closeAction={handleReload}
            confirmAction={handleReload}
            confirmText="Try again"
            isOpen={alertModalOpen}
            type="danger"
          />
        </div>
      )}
    </div>
  )
}

export default ViewRequestTask
